/**
 * Liveness Detection Service
 *
 * Detects facial actions to verify a live person vs a static photo/video.
 * Uses TensorFlow.js face-detection with MediaPipe Face Mesh for landmarks.
 *
 * Supported challenges:
 * - blink: Detect eye blink (eye aspect ratio change)
 * - turn_left: Detect head turn to the left
 * - turn_right: Detect head turn to the right
 * - nod: Detect head nod (up and down motion)
 */

import { debug, measureAsync } from '../utils/debug'

// Challenge types
export type LivenessChallenge = 'blink' | 'turn_left' | 'turn_right' | 'nod'

// Result of liveness check
export interface LivenessResult {
  passed: boolean
  confidence: number
  challenge: LivenessChallenge
  error?: string
}

// Configuration
const FRAME_CAPTURE_INTERVAL = 80 // ms between frames
const MIN_FRAMES_FOR_DETECTION = 15 // Minimum frames to analyze
const MAX_FRAMES = 80 // Maximum frames to capture

// Detection thresholds
const HEAD_TURN_THRESHOLD = 0.08 // Normalized distance for head turn
const NOD_THRESHOLD = 0.05 // Normalized distance for nod
const BLINK_CLOSED_RATIO = 0.6 // Eye openness below this fraction of peak = closed
const BLINK_MIN_DROP_RATIO = 0.2 // Minimum relative drop from open to closed

// TensorFlow face detector instance
let faceDetector: any = null
let isInitializing = false

/**
 * Initialize the face detector with MediaPipe Face Mesh
 */
async function initDetector(): Promise<void> {
  if (faceDetector) return
  if (isInitializing) {
    // Wait for initialization to complete
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    return
  }

  isInitializing = true

  try {
    const faceDetection = await import('@tensorflow-models/face-detection')
    const tf = await import('@tensorflow/tfjs')

    // Set backend (prefer WebGL for performance)
    await tf.setBackend('webgl')
    await tf.ready()

    debug.log('[Liveness] Creating MediaPipe Face Mesh detector...')

    faceDetector = await faceDetection.createDetector(
      faceDetection.SupportedModels.MediaPipeFaceDetector,
      {
        runtime: 'tfjs',
        modelType: 'full',
        maxFaces: 1,
      }
    )

    debug.log('[Liveness] Detector initialized successfully')
  } catch (error) {
    debug.error('[Liveness] Failed to initialize detector:', error)
    throw error
  } finally {
    isInitializing = false
  }
}

/**
 * Frame data for analysis
 */
interface FrameData {
  timestamp: number
  faceBox: { xMin: number; xMax: number; yMin: number; yMax: number }
  noseTip?: { x: number; y: number }
  /** Normalized eye-region contrast (higher = eyes more open) */
  eyeOpenness?: number
  landmarks?: { x: number; y: number; z?: number }[]
}

/**
 * Measure eye openness from pixel contrast around eye keypoints.
 * Closed eyes produce a smoother, lower-contrast patch than open eyes.
 */
function measureEyeOpenness(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  leftEye?: { x: number; y: number },
  rightEye?: { x: number; y: number }
): number | undefined {
  if (!leftEye || !rightEye) return undefined

  const patchSize = Math.max(16, Math.round(Math.min(canvasWidth, canvasHeight) * 0.06))

  const measurePatch = (x: number, y: number): number => {
    const px = Math.max(0, Math.min(canvasWidth - patchSize, Math.floor(x - patchSize / 2)))
    const py = Math.max(0, Math.min(canvasHeight - patchSize, Math.floor(y - patchSize / 2)))
    const imageData = ctx.getImageData(px, py, patchSize, patchSize)
    const data = imageData.data
    let sum = 0
    let sumSq = 0
    const n = data.length / 4
    for (let i = 0; i < data.length; i += 4) {
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
      sum += lum
      sumSq += lum * lum
    }
    const mean = sum / n
    const variance = Math.max(0, sumSq / n - mean * mean)
    return Math.sqrt(variance)
  }

  return (measurePatch(leftEye.x, leftEye.y) + measurePatch(rightEye.x, rightEye.y)) / 2
}

/**
 * Capture frames from video element
 */
async function captureFrames(
  videoElement: HTMLVideoElement,
  durationMs: number,
  onProgress?: (progress: number) => void
): Promise<FrameData[]> {
  const frames: FrameData[] = []
  const startTime = Date.now()
  const endTime = startTime + durationMs

  // Create canvas for frame capture
  const canvas = document.createElement('canvas')
  canvas.width = videoElement.videoWidth || 640
  canvas.height = videoElement.videoHeight || 480
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Could not get canvas context')
  }

  await initDetector()

  while (Date.now() < endTime && frames.length < MAX_FRAMES) {
    const elapsed = Date.now() - startTime
    onProgress?.(elapsed / durationMs)

    try {
      // Draw current frame
      ctx.drawImage(videoElement, 0, 0)

      // Detect faces
      const detections = await faceDetector.estimateFaces(canvas, {
        flipHorizontal: false,
      })

      if (detections && detections.length > 0) {
        const detection = detections[0]

        const frameData: FrameData = {
          timestamp: Date.now() - startTime,
          faceBox: {
            xMin: detection.box.xMin,
            xMax: detection.box.xMax,
            yMin: detection.box.yMin,
            yMax: detection.box.yMax,
          },
        }

        // Extract keypoints if available
        if (detection.keypoints && detection.keypoints.length > 0) {
          frameData.landmarks = detection.keypoints

          // Find nose tip (usually index 2 in basic keypoints)
          const noseTipKeypoint = detection.keypoints.find(
            (kp: { name?: string }) => kp.name === 'noseTip'
          )
          if (noseTipKeypoint) {
            frameData.noseTip = {
              x: noseTipKeypoint.x / canvas.width,
              y: noseTipKeypoint.y / canvas.height,
            }
          }

          // For blink detection, use eye keypoints
          const leftEye = detection.keypoints.find(
            (kp: { name?: string }) => kp.name === 'leftEye'
          )
          const rightEye = detection.keypoints.find(
            (kp: { name?: string }) => kp.name === 'rightEye'
          )

          if (leftEye && rightEye) {
            frameData.eyeOpenness = measureEyeOpenness(
              ctx,
              canvas.width,
              canvas.height,
              leftEye,
              rightEye
            )
          }
        }

        frames.push(frameData)
      }
    } catch (error) {
      debug.warn('[Liveness] Frame capture error:', error)
    }

    // Wait for next frame
    await new Promise(resolve => setTimeout(resolve, FRAME_CAPTURE_INTERVAL))
  }

  onProgress?.(1)
  return frames
}

/**
 * Detect blink action using eye-region contrast changes across frames.
 */
function detectBlink(frames: FrameData[]): { detected: boolean; confidence: number } {
  const openness = frames
    .map((f) => f.eyeOpenness)
    .filter((value): value is number => value !== undefined && value > 0)

  if (openness.length < MIN_FRAMES_FOR_DETECTION) {
    return { detected: false, confidence: 0 }
  }

  const peakOpen = Math.max(...openness)
  const minOpen = Math.min(...openness)
  const dropRatio = peakOpen > 0 ? (peakOpen - minOpen) / peakOpen : 0
  const closedThreshold = peakOpen * BLINK_CLOSED_RATIO

  let blinkCycles = 0
  let wasOpen = openness[0] >= closedThreshold
  let sawClosed = false

  for (let i = 1; i < openness.length; i++) {
    const value = openness[i]
    if (wasOpen && value < closedThreshold) {
      sawClosed = true
      wasOpen = false
    } else if (sawClosed && value >= closedThreshold) {
      blinkCycles++
      wasOpen = true
      sawClosed = false
    }
  }

  const detected = blinkCycles >= 1 && dropRatio >= BLINK_MIN_DROP_RATIO
  const confidence = Math.min(1, blinkCycles * 0.45 + dropRatio)

  return { detected, confidence }
}

/**
 * Detect head turn (left or right)
 */
function detectHeadTurn(
  frames: FrameData[],
  direction: 'left' | 'right'
): { detected: boolean; confidence: number } {
  if (frames.length < MIN_FRAMES_FOR_DETECTION) {
    return { detected: false, confidence: 0 }
  }

  const noseTips = frames.filter(f => f.noseTip).map(f => ({
    ...f.noseTip!,
    timestamp: f.timestamp,
  }))

  if (noseTips.length < MIN_FRAMES_FOR_DETECTION) {
    // Fallback to face box center
    const faceBoxCenters = frames.map(f => ({
      x: (f.faceBox.xMin + f.faceBox.xMax) / 2,
      timestamp: f.timestamp,
    }))

    // Normalize by face width
    const avgWidth = frames.reduce((a, f) => a + (f.faceBox.xMax - f.faceBox.xMin), 0) / frames.length

    // Calculate movement
    const startX = faceBoxCenters.slice(0, 5).reduce((a, b) => a + b.x, 0) / 5
    const endX = faceBoxCenters.slice(-5).reduce((a, b) => a + b.x, 0) / 5
    const movement = (endX - startX) / avgWidth

    // For turn_left, we expect negative movement (face moves right as they turn left)
    // For turn_right, we expect positive movement
    const expectedDirection = direction === 'left' ? -1 : 1
    const normalizedMovement = movement * expectedDirection

    const detected = normalizedMovement > HEAD_TURN_THRESHOLD
    const confidence = Math.min(1, Math.max(0, normalizedMovement / HEAD_TURN_THRESHOLD))

    return { detected, confidence }
  }

  // Use nose tip for more accurate detection
  const startPos = noseTips.slice(0, 5).reduce((a, b) => a + b.x, 0) / 5
  const endPos = noseTips.slice(-5).reduce((a, b) => a + b.x, 0) / 5
  const movement = endPos - startPos

  // For turn_left (user's perspective), the nose moves to the right in the video
  // (video is usually mirrored)
  const expectedDirection = direction === 'left' ? 1 : -1
  const normalizedMovement = movement * expectedDirection

  const detected = normalizedMovement > HEAD_TURN_THRESHOLD
  const confidence = Math.min(1, Math.max(0, normalizedMovement / HEAD_TURN_THRESHOLD))

  return { detected, confidence }
}

/**
 * Detect head nod (up and down motion)
 */
function detectNod(frames: FrameData[]): { detected: boolean; confidence: number } {
  if (frames.length < MIN_FRAMES_FOR_DETECTION) {
    return { detected: false, confidence: 0 }
  }

  const noseTips = frames.filter(f => f.noseTip).map(f => ({
    ...f.noseTip!,
    timestamp: f.timestamp,
  }))

  if (noseTips.length < MIN_FRAMES_FOR_DETECTION) {
    // Fallback to face box vertical center
    const faceBoxCenters = frames.map(f => ({
      y: (f.faceBox.yMin + f.faceBox.yMax) / 2,
      timestamp: f.timestamp,
    }))

    // Look for up-down-up pattern or down-up-down pattern
    const yValues = faceBoxCenters.map(f => f.y)
    const avgHeight = frames.reduce((a, f) => a + (f.faceBox.yMax - f.faceBox.yMin), 0) / frames.length

    // Find local extrema
    const extrema: { index: number; value: number; type: 'min' | 'max' }[] = []
    for (let i = 2; i < yValues.length - 2; i++) {
      const prev = yValues[i - 1]
      const curr = yValues[i]
      const next = yValues[i + 1]

      if (curr < prev && curr < next) {
        extrema.push({ index: i, value: curr, type: 'min' })
      } else if (curr > prev && curr > next) {
        extrema.push({ index: i, value: curr, type: 'max' })
      }
    }

    // Look for alternating min/max pattern with sufficient amplitude
    let nodCount = 0
    for (let i = 0; i < extrema.length - 1; i++) {
      if (extrema[i].type !== extrema[i + 1].type) {
        const amplitude = Math.abs(extrema[i].value - extrema[i + 1].value) / avgHeight
        if (amplitude > NOD_THRESHOLD) {
          nodCount++
        }
      }
    }

    const detected = nodCount >= 2
    const confidence = Math.min(1, nodCount / 3)

    return { detected, confidence }
  }

  // Use nose tip for nod detection
  const yValues = noseTips.map(t => t.y)

  // Find local extrema
  const extrema: { index: number; value: number; type: 'min' | 'max' }[] = []
  for (let i = 2; i < yValues.length - 2; i++) {
    const prev = yValues[i - 1]
    const curr = yValues[i]
    const next = yValues[i + 1]

    if (curr < prev && curr < next) {
      extrema.push({ index: i, value: curr, type: 'min' })
    } else if (curr > prev && curr > next) {
      extrema.push({ index: i, value: curr, type: 'max' })
    }
  }

  // Look for alternating min/max pattern with sufficient amplitude
  let nodCount = 0
  for (let i = 0; i < extrema.length - 1; i++) {
    if (extrema[i].type !== extrema[i + 1].type) {
      const amplitude = Math.abs(extrema[i].value - extrema[i + 1].value)
      if (amplitude > NOD_THRESHOLD) {
        nodCount++
      }
    }
  }

  const detected = nodCount >= 2
  const confidence = Math.min(1, nodCount / 3)

  return { detected, confidence }
}

/**
 * Run liveness detection with a specific challenge
 */
export async function runLivenessCheck(
  videoElement: HTMLVideoElement,
  challenge: LivenessChallenge,
  durationMs: number = 4000,
  onProgress?: (progress: number) => void
): Promise<LivenessResult> {
  const captureDuration = challenge === 'blink' ? Math.max(durationMs, 6000) : durationMs

  return measureAsync(`Liveness Check (${challenge})`, async () => {
    try {
      debug.log(`[Liveness] Starting ${challenge} detection...`)

      // Capture frames
      const frames = await captureFrames(videoElement, captureDuration, onProgress)

      debug.log(`[Liveness] Captured ${frames.length} frames`)

      if (frames.length < MIN_FRAMES_FOR_DETECTION) {
        return {
          passed: false,
          confidence: 0,
          challenge,
          error: 'Not enough frames captured. Please ensure your face is visible.',
        }
      }

      // Analyze based on challenge type
      let result: { detected: boolean; confidence: number }

      switch (challenge) {
        case 'blink':
          result = detectBlink(frames)
          break
        case 'turn_left':
          result = detectHeadTurn(frames, 'left')
          break
        case 'turn_right':
          result = detectHeadTurn(frames, 'right')
          break
        case 'nod':
          result = detectNod(frames)
          break
        default:
          return {
            passed: false,
            confidence: 0,
            challenge,
            error: `Unknown challenge type: ${challenge}`,
          }
      }

      debug.log(`[Liveness] ${challenge} result:`, result)

      return {
        passed: result.detected,
        confidence: result.confidence,
        challenge,
      }
    } catch (error) {
      debug.error('[Liveness] Detection error:', error)
      return {
        passed: false,
        confidence: 0,
        challenge,
        error: error instanceof Error ? error.message : 'Liveness detection failed',
      }
    }
  })
}

/**
 * Get a random challenge (only blink and nod for simplicity)
 */
export function getRandomChallenge(): LivenessChallenge {
  const challenges: LivenessChallenge[] = ['blink', 'nod']
  return challenges[Math.floor(Math.random() * challenges.length)]
}

/**
 * Get human-readable instruction for a challenge
 */
export function getChallengeInstruction(challenge: LivenessChallenge): string {
  switch (challenge) {
    case 'blink':
      return 'Look at the camera and blink your eyes clearly 2–3 times.'
    case 'turn_left':
      return 'Slowly turn your head to your left, then back to center.'
    case 'turn_right':
      return 'Slowly turn your head to your right, then back to center.'
    case 'nod':
      return 'Nod your head up and down 2–3 times while facing the camera.'
    default:
      return 'Follow the on-screen instructions.'
  }
}

/**
 * Get short label for a challenge (for UI display)
 */
export function getChallengeLabel(challenge: LivenessChallenge): string {
  switch (challenge) {
    case 'blink':
      return 'Blink'
    case 'turn_left':
      return 'Turn Left'
    case 'turn_right':
      return 'Turn Right'
    case 'nod':
      return 'Nod'
    default:
      return 'Action'
  }
}

/**
 * Check if the detector is ready
 */
export function isLivenessDetectorReady(): boolean {
  return faceDetector !== null
}

/**
 * Pre-initialize the detector (optional, called automatically on first use)
 */
export async function initLivenessDetector(): Promise<void> {
  await initDetector()
}
